import React from "react";
import {
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SectionCard } from "./StyledComponents";

const ImportedQuestionsList = ({
  importedQuestions,
  groupedImportedByBatch,
  importedLoading,
  onRefresh,
}) => {
  return (
    <SectionCard>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#fff" }}>
              Recently Imported Questions
            </Typography>
            <Typography variant="body2" sx={{ color: "#888", mt: 0.5 }}>
              Grouped by generation batch so you can quickly review what went
              live together.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${importedQuestions.length || 0} total`}
              size="small"
              sx={{
                backgroundColor: "rgba(76, 175, 80, 0.12)",
                color: "#A5D6A7",
                fontWeight: 500,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              disabled={importedLoading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
        {importedLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : importedQuestions.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              color: "#777",
              borderRadius: 2,
              border: "1px dashed #333",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(76,175,80,0.04))",
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              No imported questions yet
            </Typography>
            <Typography variant="body2">
              Once you publish AI-generated questions from the Staging tab, they
              will appear here, grouped by the batch they came from.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 520, overflow: "auto" }}>
            {Object.entries(groupedImportedByBatch).map(
              ([batchId, questions]) => {
                const batchLabel =
                  batchId === "Unassigned"
                    ? "No Source Batch"
                    : `Batch #${batchId}`;
                const firstCreated = questions[0]?.created_at
                  ? new Date(questions[0].created_at).toLocaleString()
                  : "";

                return (
                  <Accordion
                    key={batchId}
                    defaultExpanded
                    disableGutters
                    sx={{
                      backgroundColor: "#151515",
                      borderRadius: "10px !important",
                      border: "1px solid #333",
                      mb: 1.5,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#888" }} />}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ width: "100%", justifyContent: "space-between" }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={batchLabel}
                            size="small"
                            sx={{
                              backgroundColor: "#263238",
                              color: "#ECEFF1",
                              fontWeight: 600,
                            }}
                          />
                          <Chip
                            label={`${questions.length} question${
                              questions.length > 1 ? "s" : ""
                            }`}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(76, 175, 80, 0.18)",
                              color: "#C8E6C9",
                              fontWeight: 500,
                            }}
                          />
                        </Stack>
                        {firstCreated && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#888", fontStyle: "italic" }}
                          >
                            First imported: {firstCreated}
                          </Typography>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                      <Paper
                        sx={{
                          backgroundColor: "#111",
                          borderRadius: 1.5,
                          overflow: "hidden",
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: "#999", width: "45%" }}>
                                Question
                              </TableCell>
                              <TableCell sx={{ color: "#999", width: "25%" }}>
                                Song
                              </TableCell>
                              <TableCell sx={{ color: "#999", width: "15%" }}>
                                Question ID
                              </TableCell>
                              <TableCell sx={{ color: "#999", width: "15%" }}>
                                Imported At
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {questions.map((q) => (
                              <TableRow
                                key={q.question_id}
                                hover
                                sx={{
                                  "&:nth-of-type(odd)": {
                                    backgroundColor: "rgba(255,255,255,0.02)",
                                  },
                                }}
                              >
                                <TableCell sx={{ color: "#fff" }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {q.question_text}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ color: "#ccc" }}>
                                  <Typography variant="body2">
                                    {q.song_name || "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ color: "#ccc" }}>
                                  #{q.question_id}
                                </TableCell>
                                <TableCell sx={{ color: "#ccc" }}>
                                  {q.created_at
                                    ? new Date(q.created_at).toLocaleString()
                                    : "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                );
              }
            )}
          </Box>
        )}
      </CardContent>
    </SectionCard>
  );
};

export default ImportedQuestionsList;
